import { useEffect, useRef } from 'react';
import { REACTIONS, isReacted } from './NoteReactions';
import useSheetAnim from '../../hooks/useSheetAnim';
import './notes.css';

// Bottom sheet behind each row's ···. Collapsing React / Reply / Edit / Delete in
// here is what keeps the feed itself quiet — a thread that's mostly audit lines
// shouldn't carry four visible controls per entry.

const ICON = {
  reply: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 11.5a8.4 8.4 0 0 1-8.5 8.3 8.9 8.9 0 0 1-3.2-.6L3 21l1.9-5.6a8 8 0 0 1-.9-3.9A8.4 8.4 0 0 1 12.5 3a8.4 8.4 0 0 1 8.5 8.5Z" /></svg>,
  edit: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 20h4L19 9a2.8 2.8 0 0 0-4-4L4 16v4Z" /></svg>,
  copy: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="9" y="9" width="11" height="11" rx="2" /><path d="M15 5H6a2 2 0 0 0-2 2v9" /></svg>,
  trash: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 7h16M9 7V5h6v2M6 7l1 13h10l1-13" /></svg>,
};

export default function NoteActionSheet({ note, onClose, onReact, onReply, onEdit, onCopy, onDelete }) {
  // Close on Back so the sheet doesn't strand someone on a phone.
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose?.(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  // This sheet is driven by `note` rather than an open flag, so on close the prop
  // goes null. Hold the last one so there's still something to render while it
  // slides back down.
  const { mounted, closing } = useSheetAnim(!!note);
  const lastNote = useRef(note);
  if (note) lastNote.current = note;
  const shown = note || lastNote.current;

  if (!mounted || !shown) return null;

  const canEdit = !!shown.mine && !shown.is_deleted;
  const isTopLevel = !shown.parent_note_id;

  const act = (fn) => () => { onClose?.(); fn?.(shown); };

  return (
    <div className={`note-sheet-mask sheet-scrim${closing ? ' is-closing' : ''}`} onClick={onClose} role="presentation">
      <div className={`note-sheet sheet-panel${closing ? ' is-closing' : ''}`} onClick={(e) => e.stopPropagation()} role="dialog" aria-label="Comment actions">
        <div className="note-sheet-grip" />

        <div className="note-sheet-reacts">
          {REACTIONS.map((r) => (
            <button
              key={r.key}
              type="button"
              title={r.label}
              className={isReacted(shown.reactions?.[r.key]) ? 'reacted' : ''}
              onClick={() => { onClose?.(); onReact?.(shown.id, r.key, isReacted(shown.reactions?.[r.key])); }}
            >
              {r.emoji}
            </button>
          ))}
        </div>

        {isTopLevel && (
          <button type="button" className="note-sheet-act" onClick={act(onReply)}>
            {ICON.reply} Reply
          </button>
        )}
        {canEdit && (
          <button type="button" className="note-sheet-act" onClick={act(onEdit)}>
            {ICON.edit} Edit
          </button>
        )}
        <button type="button" className="note-sheet-act" onClick={act(onCopy)}>
          {ICON.copy} Copy text
        </button>
        {canEdit && (
          <button type="button" className="note-sheet-act danger" onClick={act(onDelete)}>
            {ICON.trash} Delete
          </button>
        )}
      </div>
    </div>
  );
}
